import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Users, ShieldCheck, UserCog, User, Pencil, Key, X } from 'lucide-react';

const ROLES = [
  { value: 'ANALISTA', label: 'Analista', icon: User },
  { value: 'SUPERVISOR', label: 'Supervisor', icon: UserCog },
  { value: 'ADMINISTRADOR', label: 'Administrador', icon: ShieldCheck },
];

const getRoleColor = (rol) => {
  const colors = {
    ADMINISTRADOR: 'bg-purple-100 text-purple-800',
    SUPERVISOR: 'bg-blue-100 text-blue-800',
    ANALISTA: 'bg-slate-100 text-slate-800',
  };
  return colors[rol] || colors.ANALISTA;
};

export const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    nombre: '',
    password: '',
    rol: 'ANALISTA',
    send_temp_password: false,  // Enviar clave temporal por email
  });
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    loadUsers();
    loadGroups();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('No tienes permisos para ver usuarios');
      } else {
        toast.error('Error al cargar usuarios');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await api.get('/groups/');
      setGroups(response.data);
    } catch (error) {
    }
  };

  const openCreateDialog = () => {
    setFormData({
      email: '',
      nombre: '',
      password: '',
      rol: 'ANALISTA',
      send_temp_password: false,
    });
    setIsCreateDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      nombre: user.nombre,
      password: '',
      rol: user.rol,
      send_temp_password: false,
    });
    setSelectedGroups(user.grupos || []);
    setIsEditDialogOpen(true);
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setPasswordData({ newPassword: '', confirmPassword: '' });
    setIsPasswordDialogOpen(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  const handleCreateUser = async () => {
    if (!formData.email || !formData.nombre) {
      toast.error('Por favor complete email y nombre');
      return;
    }
    
    // Si no envía clave temporal, necesita password
    if (!formData.send_temp_password && !formData.password) {
      toast.error('Ingrese una contraseña o seleccione enviar clave temporal');
      return;
    }

    if (!formData.send_temp_password && formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setSaving(true);
    try {
      await api.post('/users', formData);
      toast.success('Usuario creado correctamente');
      setIsCreateDialogOpen(false);
      loadUsers();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Error al crear usuario');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEditUser = async () => {
    if (!formData.email || !formData.nombre) {
      toast.error('Por favor complete todos los campos requeridos');
      return;
    }

    setSaving(true);
    try {
      await api.patch(`/users/${selectedUser.id}`, {
        email: formData.email,
        nombre: formData.nombre,
        rol: formData.rol,
      });
      
      // Actualizar grupos del usuario
      await api.put(`/groups/user/${selectedUser.id}/groups`, {
        group_ids: selectedGroups
      });
      
      toast.success('Usuario actualizado correctamente');
      setIsEditDialogOpen(false);
      loadUsers();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Error al actualizar usuario');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('Por favor complete ambos campos');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setSaving(true);
    try {
      await api.patch(`/users/${selectedUser.id}/password`, {
        new_password: passwordData.newPassword,
      });
      toast.success('Contraseña actualizada correctamente');
      setIsPasswordDialogOpen(false);
      setPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Error al cambiar contraseña');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    setSaving(true);
    try {
      await api.delete(`/users/${selectedUser.id}`);
      toast.success('Usuario eliminado correctamente');
      setIsDeleteDialogOpen(false);
      loadUsers();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Error al eliminar usuario');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await api.patch(`/users/${userId}/role?new_role=${newRole}`);
      toast.success('Rol actualizado correctamente');
      loadUsers();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Error al cambiar rol');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-slate-900">
            Gestión de Usuarios
          </h1>
          <p className="text-slate-600 mt-1">
            {users.length} usuario(s) registrado(s)
          </p>
        </div>
        <Button
          data-testid="create-user-button"
          onClick={openCreateDialog}
          className="mt-4 lg:mt-0"
          style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
        >
          <Plus size={18} className="mr-2" />
          Nuevo Usuario
        </Button>
      </div>

      {/* Users Table */}
      <Card className="border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuario</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Creado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  <Users size={48} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-500">No hay usuarios registrados</p>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold text-sm">
                          {user.nombre.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="font-medium text-slate-900">{user.nombre}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600">{user.email}</TableCell>
                  <TableCell>
                    <Select
                      value={user.rol}
                      onValueChange={(value) => handleChangeRole(user.id, value)}
                    >
                      <SelectTrigger className="w-[150px]">
                        <SelectValue>
                          <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${getRoleColor(user.rol)}`}>
                            {user.rol}
                          </span>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map((role) => (
                          <SelectItem key={role.value} value={role.value}>
                            <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${getRoleColor(role.value)}`}>
                              {role.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm font-mono">
                    {new Date(user.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        onClick={() => openEditDialog(user)}
                        size="sm"
                        variant="outline"
                        title="Editar usuario"
                        style={{ borderColor: '#bfdbfe', color: '#2563eb' }}
                      >
                        <Pencil size={16} style={{ color: '#2563eb' }} />
                        <span className="ml-1 text-xs" style={{ color: '#2563eb' }}>Editar</span>
                      </Button>
                      <Button
                        onClick={() => openPasswordDialog(user)}
                        size="sm"
                        variant="outline"
                        title="Cambiar contraseña"
                        style={{ borderColor: '#e9d5ff', color: '#9333ea' }}
                      >
                        <Key size={16} style={{ color: '#9333ea' }} />
                        <span className="ml-1 text-xs" style={{ color: '#9333ea' }}>Clave</span>
                      </Button>
                      <Button
                        data-testid={`delete-user-${user.id}`}
                        onClick={() => openDeleteDialog(user)}
                        size="sm"
                        variant="outline"
                        title="Eliminar usuario"
                        style={{ borderColor: '#fecaca', color: '#dc2626' }}
                      >
                        <Trash2 size={16} style={{ color: '#dc2626' }} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#0f172a' }}>Crear Nuevo Usuario</DialogTitle>
            <DialogDescription style={{ color: '#64748b' }}>
              Ingresa los datos del nuevo usuario del sistema.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label style={{ color: '#334155' }}>Nombre Completo *</Label>
              <Input
                data-testid="user-name-input"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Juan Pérez"
                style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
              />
            </div>
            <div className="space-y-2">
              <Label style={{ color: '#334155' }}>Email *</Label>
              <Input
                type="email"
                data-testid="user-email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="usuario@ejemplo.com"
                style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
              />
            </div>
            
            {/* Selector de tipo de contraseña */}
            <div className="space-y-3">
              <Label style={{ color: '#334155' }}>Método de Contraseña</Label>
              
              {/* Opción 1: Clave temporal */}
              <div 
                onClick={() => setFormData({ ...formData, send_temp_password: true, password: '' })}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.send_temp_password 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    formData.send_temp_password ? 'border-blue-500' : 'border-slate-300'
                  }`}>
                    {formData.send_temp_password && <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800">Enviar clave temporal por email</span>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Recomendado</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      Se genera una contraseña segura automáticamente y se envía al email del usuario. 
                      Deberá cambiarla en su primer inicio de sesión.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Opción 2: Clave fija */}
              <div 
                onClick={() => setFormData({ ...formData, send_temp_password: false })}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  !formData.send_temp_password 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    !formData.send_temp_password ? 'border-blue-500' : 'border-slate-300'
                  }`}>
                    {!formData.send_temp_password && <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />}
                  </div>
                  <div className="flex-1">
                    <span className="font-medium text-slate-800">Definir contraseña manualmente</span>
                    <p className="text-sm text-slate-500 mt-1">
                      Vos definís la contraseña. Deberás comunicársela al usuario por otro medio.
                    </p>
                    
                    {/* Campo de contraseña inline */}
                    {!formData.send_temp_password && (
                      <div className="mt-3">
                        <Input
                          type="password"
                          data-testid="user-password-input"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          placeholder="Mínimo 6 caracteres"
                          onClick={(e) => e.stopPropagation()}
                          className="bg-white"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label style={{ color: '#334155' }}>Rol</Label>
              <Select
                value={formData.rol}
                onValueChange={(value) => setFormData({ ...formData, rol: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar rol" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex items-center">
                        <role.icon size={16} className="mr-2" />
                        {role.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={saving}
              style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
            >
              {saving ? 'Guardando...' : 'Crear Usuario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#0f172a' }}>Editar Usuario</DialogTitle>
            <DialogDescription style={{ color: '#64748b' }}>
              Modifica los datos del usuario.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label style={{ color: '#334155' }}>Nombre Completo *</Label>
              <Input
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Juan Pérez"
                style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
              />
            </div>
            <div className="space-y-2">
              <Label style={{ color: '#334155' }}>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="usuario@ejemplo.com"
                style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
              />
            </div>
            <div className="space-y-2">
              <Label style={{ color: '#334155' }}>Rol</Label>
              <Select
                value={formData.rol}
                onValueChange={(value) => setFormData({ ...formData, rol: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar rol" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex items-center">
                        <role.icon size={16} className="mr-2" />
                        {role.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Grupos del Usuario */}
            <div className="space-y-2">
              <Label style={{ color: '#334155' }}>Grupos</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {selectedGroups.map(groupId => {
                  const group = groups.find(g => g.id === groupId);
                  if (!group) return null;
                  return (
                    <Badge 
                      key={groupId}
                      className="flex items-center gap-1 cursor-pointer"
                      style={{ backgroundColor: `${group.color}20`, color: group.color }}
                      onClick={() => setSelectedGroups(selectedGroups.filter(id => id !== groupId))}
                    >
                      {group.nombre}
                      <X size={12} />
                    </Badge>
                  );
                })}
                {selectedGroups.length === 0 && (
                  <span className="text-xs text-slate-400">Sin grupos asignados</span>
                )}
              </div>
              <Select
                onValueChange={(value) => {
                  if (!selectedGroups.includes(value)) {
                    setSelectedGroups([...selectedGroups, value]);
                  }
                }}
              >
                <SelectTrigger style={{ backgroundColor: '#f8fafc' }}>
                  <SelectValue placeholder="Agregar a grupo..." />
                </SelectTrigger>
                <SelectContent>
                  {groups.filter(g => !selectedGroups.includes(g.id)).map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: group.color }}
                        />
                        {group.nombre}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleEditUser}
              disabled={saving}
              style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
            >
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent className="max-w-md" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#0f172a' }}>Cambiar Contraseña</DialogTitle>
            <DialogDescription style={{ color: '#64748b' }}>
              Cambiar contraseña de {selectedUser?.nombre}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label style={{ color: '#334155' }}>Nueva Contraseña *</Label>
              <Input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                placeholder="Mínimo 6 caracteres"
                style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
              />
            </div>
            <div className="space-y-2">
              <Label style={{ color: '#334155' }}>Confirmar Contraseña *</Label>
              <Input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                placeholder="Repita la contraseña"
                style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPasswordDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleChangePassword}
              disabled={saving}
              style={{ backgroundColor: '#9333ea', color: '#ffffff' }}
            >
              {saving ? 'Guardando...' : 'Cambiar Contraseña'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#0f172a' }}>Eliminar Usuario</DialogTitle>
            <DialogDescription style={{ color: '#64748b' }}>
              ¿Estás seguro de que deseas eliminar al usuario "{selectedUser?.nombre}"? 
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteUser}
              disabled={saving}
              style={{ backgroundColor: '#dc2626', color: '#ffffff' }}
            >
              {saving ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
